import { Component, Input } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { NbDialogRef } from '@nebular/theme';
import { IBucket, isSystemBucket } from '../../../services/bucket.service';
import { Tutor } from '../../../services/tutor.service';

/** `Validators.required` accepts whitespace-only input, but the dialog trims
 * before submitting — so blank strings must count as missing too. Reports the
 * plain `required` error so the global required-field feedback paints it. */
const requiredNonBlank: ValidatorFn = (control) =>
  typeof control.value === 'string' && control.value.trim() === ''
    ? { required: true }
    : null;

/** scratch = empty exercise via the API; kb = AI from chosen knowledge bases;
 *  topic = AI on the described topic, no knowledge base (Creator › Neue Übung). */
export type ExerciseCreateMode = 'scratch' | 'kb' | 'topic';

export interface ExerciseCreateConfig {
  mode: ExerciseCreateMode;
  title: string;
  description: string;
  exerciseType: string;
  exerciseTypes: string[];
  difficultyLevel: string;
  learningGoals: string;
  scope: string;
  maxScore: number;
  selectedBucketIds: string[];
}

/** Query parameters that start the AI exercise in the Creator workspace
 *  (canvas `mode=exercise`); the description is required there — the
 *  generator refuses to run without it (#223). */
export function exerciseWorkspaceParams(config: ExerciseCreateConfig): Record<string, string> {
  const params: Record<string, string> = {
    mode: 'exercise',
    title: config.title,
    description: config.description,
    exerciseType: config.exerciseType,
    exerciseTypes: config.exerciseTypes.join(','),
    difficultyLevel: config.difficultyLevel,
    learningGoals: config.learningGoals,
    scope: config.scope,
  };
  if (config.selectedBucketIds.length) params['bucketIds'] = config.selectedBucketIds.join(',');
  return params;
}

@Component({
  selector: 'app-exercise-create-dialog',
  templateUrl: './exercise-create-dialog.component.html',
  styleUrl: './exercise-create-dialog.component.scss',
})
export class ExerciseCreateDialogComponent {
  @Input() mode: ExerciseCreateMode = 'scratch';
  @Input() tutors: Tutor[] = [];
  @Input() buckets: IBucket[] = [];
  @Input() onConfirm: (config: ExerciseCreateConfig) => void = () => {};
  @Input() participantUserId?: string;
  @Input() participantName?: string;

  // Step tracking
  step = 1;

  // Step 1 — required controls carry `Validators.required` so the global
  // `RequiredFieldFeedbackDirective` paints them red + shakes once the user
  // touches them or tries to continue (design.md §5 "Required-field feedback").
  basicsForm = new FormGroup({
    title: new FormControl<string>('', { nonNullable: true, validators: requiredNonBlank }),
    description: new FormControl<string>('', { nonNullable: true, validators: requiredNonBlank }),
    learningGoals: new FormControl<string>('', { nonNullable: true }),
  });

  // Exercise parameters step — `exerciseTypes` (multi-select) can be emptied
  // by the user, `maxScore` can be cleared; both stay required.
  paramsForm = new FormGroup({
    exerciseTypes: new FormControl<string[]>(['multiple-choice'], {
      nonNullable: true,
      validators: Validators.required,
    }),
    difficultyLevel: new FormControl<string>('medium', { nonNullable: true, validators: Validators.required }),
    scope: new FormControl<string>('medium', { nonNullable: true, validators: Validators.required }),
    maxScore: new FormControl<number | null>(100, Validators.required),
  });

  // KB selection
  selectedBucketIds: string[] = [];

  exerciseTypes = [
    { value: 'multiple-choice', label: 'Multiple Choice' },
    { value: 'open-ended', label: 'Open Ended' },
    { value: 'fill-in-blank', label: 'Fill in the Blank' },
    { value: 'true-false', label: 'True / False' },
    { value: 'exam', label: 'Exam' },
  ];

  difficultyLevels = [
    { value: 'easy', labelKey: 'pages.dozent.exerciseDialog.easy' },
    { value: 'medium', labelKey: 'pages.dozent.exerciseDialog.medium' },
    { value: 'hard', labelKey: 'pages.dozent.exerciseDialog.hard' },
  ];

  scopeOptions = [
    { value: 'small', labelKey: 'pages.dozent.exerciseDialog.scopeSmall' },
    { value: 'medium', labelKey: 'pages.dozent.exerciseDialog.scopeMedium' },
    { value: 'large', labelKey: 'pages.dozent.exerciseDialog.scopeLarge' },
  ];

  constructor(
    protected dialogRef: NbDialogRef<ExerciseCreateDialogComponent>
  ) {}

  get totalSteps(): number {
    return this.mode === 'kb' ? 3 : 2;
  }

  get isKbSelectionStep(): boolean {
    return this.mode === 'kb' && this.step === 2;
  }

  get isParamsStep(): boolean {
    return (
      (this.mode !== 'kb' && this.step === 2) ||
      (this.mode === 'kb' && this.step === 3)
    );
  }

  /** The customer's own folders; internal ones (`__…`) are not offered (#224). */
  get connectedBuckets(): IBucket[] {
    if (this.buckets !== this.bucketsSource) {
      this.bucketsSource = this.buckets;
      this.visibleBuckets = (this.buckets ?? []).filter((b) => !isSystemBucket(b));
    }
    return this.visibleBuckets;
  }

  // the filtered list is kept until the input changes (one array per input)
  private bucketsSource?: IBucket[];
  private visibleBuckets: IBucket[] = [];

  toggleBucket(bucketId: string) {
    const idx = this.selectedBucketIds.indexOf(bucketId);
    if (idx >= 0) {
      this.selectedBucketIds.splice(idx, 1);
    } else {
      this.selectedBucketIds.push(bucketId);
    }
  }

  isBucketSelected(bucketId: string): boolean {
    return this.selectedBucketIds.includes(bucketId);
  }

  /** The KB list is a custom checkbox surface without an input control the
   * global required-field feedback could paint — so this step keeps gating
   * the button until at least one knowledge base is picked. */
  canProceed(): boolean {
    if (this.isKbSelectionStep) {
      return this.selectedBucketIds.length > 0;
    }
    return true;
  }

  nextStep() {
    // Form steps stay clickable; an invalid form marks its controls touched
    // so the missing required fields light up instead of a silently
    // disabled button.
    if (this.step === 1 && this.basicsForm.invalid) {
      this.basicsForm.markAllAsTouched();
      return;
    }
    if (this.isParamsStep && this.paramsForm.invalid) {
      this.paramsForm.markAllAsTouched();
      return;
    }
    if (!this.canProceed()) {
      return;
    }
    if (this.step < this.totalSteps) {
      this.step++;
    } else {
      this.submit();
    }
  }

  prevStep() {
    if (this.step > 1) {
      this.step--;
    }
  }

  submit() {
    const basics = this.basicsForm.getRawValue();
    const params = this.paramsForm.getRawValue();
    const exerciseTypes = params.exerciseTypes.length
      ? params.exerciseTypes
      : ['multiple-choice'];
    const primaryExerciseType = exerciseTypes[0];

    this.onConfirm({
      mode: this.mode,
      title: basics.title.trim(),
      description: basics.description.trim(),
      exerciseType: primaryExerciseType,
      exerciseTypes,
      difficultyLevel: params.difficultyLevel,
      learningGoals: basics.learningGoals,
      scope: params.scope,
      maxScore: params.maxScore ?? 100,
      selectedBucketIds: this.selectedBucketIds,
    });
    this.dialogRef.close();
  }

  close() {
    this.dialogRef.close();
  }
}
